import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import css from './views.module.css'
import { Card, ViewHeader, Empty, QueryError, Disclosure } from '../ui'
import { useDepartments, useEvents, useEventTypes, useRuns } from '../data/queries'
import { toActivity, matchesQuery } from '../data/map/activity'
import { departmentsFrom } from '../data/map/department'
import { activityDayLine, activityDays } from '../data/map/summaries'
import { EMPTY_REGISTRY, toRegistry, type Lens } from '../data/map/eventTypes'
import type { AgentRunRow } from '../lib/database.types'

/**
 * What each lens shows, as the header says it and as the pill's title explains
 * it. Words a person taking the system over reads without training.
 */
const LENSES: ReadonlyArray<{ lens: Lens; label: string; aside: string; title: string }> = [
  {
    lens: 'story',
    label: 'Story',
    aside: 'heartbeats and raw Linear deliveries left out',
    title: 'What happened to each piece of work, gate and finding',
  },
  {
    lens: 'milestones',
    label: 'Milestones',
    aside: 'milestones only',
    title: 'Only the key beats: work picked up, pull requests, reviews, failures, decisions, data issues',
  },
  {
    lens: 'all',
    label: 'Everything',
    aside: 'everything, including heartbeats and raw Linear deliveries',
    title: 'Every row the fleet has written, for debugging the plumbing',
  },
]

/** `?internal=1` was the pill this replaced; its bookmarks meant "show everything". */
function lensFrom(params: URLSearchParams): Lens {
  const lens = params.get('lens')
  if (lens === 'milestones' || lens === 'all') return lens
  return params.get('internal') === '1' ? 'all' : 'story'
}

/**
 * Append-only, newest first, quiet. The department filter and the search term
 * live in the URL because the spec shares one filter across approvals, activity
 * and mobile, and because a filtered feed is worth being able to send to someone.
 */
export function Activity() {
  const [params, setParams] = useSearchParams()
  const dept = params.get('dept') ?? 'All'
  const query = params.get('q') ?? ''
  // In the URL with the other two filters, and for the same reason: it changes
  // what the page shows, so it should survive a reload and be sendable.
  const lens = lensFrom(params)

  const types = useEventTypes()
  const registry = useMemo(() => (types.data ? toRegistry(types.data) : EMPTY_REGISTRY), [types.data])
  // Settled either way: a registry that failed to load leaves the feed on the
  // filtering it can do without one, rather than leaving it blank.
  const registryReady = types.isSuccess || types.isError
  const unpublished = registryReady && registry.defs.length === 0

  const events = useEvents(registry, lens, registryReady)
  const runs = useRuns()
  const depts = useDepartments()

  const { refByRunId, runsById } = useMemo(() => {
    const refs = new Map<string, { ref: string | null; url: string | null }>()
    const byId = new Map<string, AgentRunRow>()
    for (const run of runs.data ?? []) {
      refs.set(run.id, { ref: run.external_ref, url: run.external_url ?? run.pr_url })
      byId.set(run.id, run)
    }
    return { refByRunId: refs, runsById: byId }
  }, [runs.data])

  const rows = useMemo(
    () => (events.data ? toActivity(events.data, refByRunId, registry, runsById, lens) : []),
    [events.data, refByRunId, registry, runsById, lens],
  )
  const lensInfo = LENSES.find((l) => l.lens === lens)!

  const departments = useMemo(
    () => departmentsFrom(depts.data ?? [], events.data ?? []),
    [depts.data, events.data],
  )
  const visible = rows.filter((r) => (dept === 'All' || r.department === dept) && matchesQuery(r, query))
  const days = activityDays(visible)

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (!value || value === 'All') next.delete(key)
    else next.set(key, value)
    // The lens replaced `internal`; drop the old key so the two cannot disagree.
    if (key === 'lens') next.delete('internal')
    setParams(next, { replace: true })
  }

  if (events.error) return <QueryError error={events.error} />

  return (
    <>
      <ViewHeader
        title="Activity"
        aside={
          events.data
            ? `Append-only · newest first · ${visible.length} of ${rows.length} ${rows.length === 1 ? 'event' : 'events'} · ${
                lensInfo.aside
              }${unpublished ? ' · event registry not published yet, showing raw types' : ''}`
            : 'Append-only · newest first'
        }
      />

      <div className={css.searchRow} style={{ marginBottom: 14 }}>
        <input
          className={`${css.input} ${css.search}`}
          type="search"
          value={query}
          placeholder="Search the feed"
          aria-label="Search activity"
          onChange={(e) => setParam('q', e.target.value)}
        />
        <div className={css.filters}>
          <button
            className={dept === 'All' ? `${css.filterPill} ${css.filterActive}` : css.filterPill}
            onClick={() => setParam('dept', 'All')}
          >
            All departments
          </button>
          {departments.map((d) => (
            <button
              key={d.slug}
              className={dept === d.slug ? `${css.filterPill} ${css.filterActive}` : css.filterPill}
              onClick={() => setParam('dept', d.slug)}
            >
              {d.label}
            </button>
          ))}
        </div>
        {/* Its own group rather than another pill in the department row: it is
            a lens, not a department, and sitting among them would read as one.
            Leaving rows out is not hiding them -- the header says which lens is
            on, and Everything brings every row back. */}
        <div className={css.filters} role="group" aria-label="Which events to show">
          {LENSES.map((l) => (
            <button
              key={l.lens}
              className={lens === l.lens ? `${css.filterPill} ${css.filterActive}` : css.filterPill}
              aria-pressed={lens === l.lens}
              onClick={() => setParam('lens', l.lens === 'story' ? '' : l.lens)}
              title={l.title}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <div className={css.feed}>
          {visible.length === 0 ? (
            <Empty glyph="[-_-]">
              {rows.length === 0
                ? 'Nothing here yet. The feed fills itself as agents work.'
                : 'Nothing matches that. The feed itself is unchanged.'}
            </Empty>
          ) : (
            // A day per section, newest open and the rest closed to a count. A
            // search opens every day it matched in: the matches are the answer,
            // and making someone open each day to find them would hide it.
            days.map((day, index) => (
              <div key={`${day.date}:${query ? 'q' : ''}`} className={css.feedDay}>
                <Disclosure
                  label
                  title={day.isToday ? 'Today' : day.date}
                  summary={activityDayLine(day)}
                  defaultOpen={index === 0 || query !== ''}
                >
                  {day.rows.map((row) => (
                    <div key={row.id} className={css.feedRow}>
                      <span className={`${css.feedTime} mono`} title={row.date}>
                        {/* The day is the section's heading now, so every row can say its time. */}
                        {row.time}
                      </span>
                      <span className={css.feedDept}>{row.department}</span>
                      <span className={css.feedText}>
                        {row.text}
                        {/* Two Linear deliveries a tenth of a second apart carry the
                            same summary and different changes. Saying which field
                            moved is what stops them reading as duplicates. */}
                        {row.changed ? <span className={css.feedChanged}>· {row.changed}</span> : null}
                      </span>
                      <span className={`${css.feedRef} mono`}>
                        {row.url ? (
                          <a href={row.url} target="_blank" rel="noreferrer">{row.ref}</a>
                        ) : (
                          row.ref
                        )}
                      </span>
                    </div>
                  ))}
                </Disclosure>
              </div>
            ))
          )}
        </div>
      </Card>
    </>
  )
}
